/**
 * SchemaMapperModal - Modal for mapping spreadsheet columns to workout fields
 * Supports wide-format spreadsheets with up to 5 sets per row
 */

import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X, Check, Wand2, Loader2, ChevronLeft, Import, Plus, Minus, Save, User, Users } from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import { useCoachStore } from '../../stores/coachStore';
import { fitness } from '../../services/supabaseService';
import { coachRelationships } from '../../services/coachService';
import { EXERCISES } from '../../data/exercises';
import toast from 'react-hot-toast';

const DEFAULT_MAPPING = {
  date_column: '',
  exercise_column: '',
  notes_column: '',
  set1_reps: '',
  set1_weight: '',
  set2_reps: '',
  set2_weight: '',
  set3_reps: '',
  set3_weight: '',
  set4_reps: '',
  set4_weight: '',
  set5_reps: '',
  set5_weight: '',
  header_row: 1,
  data_start_row: 2,
  numSets: 3,
};

const SchemaMapperModal = ({ isOpen, onClose }) => {
  const {
    schemaMapping,
    closeSchemaMapper,
    loadWorkouts,
    loadConnectedSpreadsheets,
  } = useFitnessStore();

  const { viewingClientId } = useCoachStore();

  const [numSets, setNumSets] = useState(3);
  const [isImporting, setIsImporting] = useState(false);
  const [hasSavedMapping, setHasSavedMapping] = useState(false);
  const [normalizeExercises, setNormalizeExercises] = useState(true); // AI normalization enabled by default
  const [isNormalizing, setIsNormalizing] = useState(false);
  const [dateFilter, setDateFilter] = useState(''); // Date filter: only import records after this date

  // Client/Coach selection
  const [clients, setClients] = useState([]);  // For coaches - their clients
  const [coaches, setCoaches] = useState([]);  // For clients - their coaches
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedCoachId, setSelectedCoachId] = useState('');
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(false);

  const [mapping, setMapping] = useState(DEFAULT_MAPPING);

  // Load saved mapping when modal opens
  useEffect(() => {
    if (schemaMapping?.savedMapping) {
      const saved = schemaMapping.savedMapping;
      setMapping(prev => ({
        ...prev,
        ...saved,
      }));
      if (saved.numSets) {
        setNumSets(saved.numSets);
      }
      setHasSavedMapping(true);
      toast.success('Önceki eşleştirme yüklendi');
    } else {
      setMapping(DEFAULT_MAPPING);
      setNumSets(3);
      setHasSavedMapping(false);
    }
  }, [schemaMapping?.spreadsheetId]);

  // Load both clients (for coaches) and coaches (for clients)
  useEffect(() => {
    const loadRelationships = async () => {
      if (!isOpen) return;

      setIsLoadingRelationships(true);
      try {
        // Load clients (if user is a coach)
        console.log('[SchemaMapper] Loading clients...');
        const clientsList = await coachRelationships.getMyClients();
        console.log('[SchemaMapper] Clients loaded:', clientsList);
        setClients(clientsList || []);

        // Load coaches (if user is a client)
        console.log('[SchemaMapper] Loading coaches...');
        const coachesList = await coachRelationships.getMyCoaches();
        console.log('[SchemaMapper] Coaches loaded:', coachesList);
        setCoaches(coachesList || []);

        // Auto-select if viewing a specific client (coach mode)
        if (viewingClientId) {
          console.log('[SchemaMapper] Auto-selecting viewingClientId:', viewingClientId);
          setSelectedClientId(viewingClientId);
        } else if (clientsList?.length === 1) {
          // Auto-select if only one client
          setSelectedClientId(clientsList[0].client_id);
        }

        // Auto-select coach if only one
        if (coachesList?.length === 1) {
          setSelectedCoachId(coachesList[0].coach_id);
        }
      } catch (error) {
        console.error('[SchemaMapper] Error loading relationships:', error);
        setClients([]);
        setCoaches([]);
      } finally {
        setIsLoadingRelationships(false);
      }
    };

    loadRelationships();
  }, [isOpen, viewingClientId]);

  // Extract column letters from data (support up to AZ)
  const columns = useMemo(() => {
    if (!schemaMapping?.values?.[0]) return [];
    const numCols = schemaMapping.values[0].length;
    const cols = [];
    for (let i = 0; i < numCols; i++) {
      if (i < 26) {
        cols.push(String.fromCharCode(65 + i));
      } else {
        cols.push('A' + String.fromCharCode(65 + (i - 26)));
      }
    }
    return cols;
  }, [schemaMapping]);

  // Get header values for column selection display
  const headerValues = useMemo(() => {
    if (!schemaMapping?.values) return {};
    const headerRow = schemaMapping.values[mapping.header_row - 1] || [];
    const result = {};
    headerRow.forEach((value, idx) => {
      const col = idx < 26 ? String.fromCharCode(65 + idx) : 'A' + String.fromCharCode(65 + (idx - 26));
      result[col] = value || '';
    });
    return result;
  }, [schemaMapping, mapping.header_row]);

  // Get column index from letter
  const getColumnIndex = (col) => {
    if (!col) return -1;
    if (col.length === 1) {
      return col.charCodeAt(0) - 65;
    } else {
      return 26 + (col.charCodeAt(1) - 65);
    }
  };

  const handleColumnSelect = (field, column) => {
    setMapping(prev => ({ ...prev, [field]: column }));
  };

  // Parse and import workouts based on mapping
  const handleImport = async () => {
    if (!mapping.date_column || !mapping.exercise_column) {
      toast.error('Tarih ve Egzersiz sütunlarını seçmelisiniz');
      return;
    }

    setIsImporting(true);
    try {
      // Fetch all data from sheet
      toast.loading('Veriler okunuyor...', { id: 'import' });
      const fullData = await fitness.readSheetData(schemaMapping.spreadsheetId, 'A1:AZ1000');
      toast.dismiss('import');

      if (!fullData?.values?.length) {
        throw new Error('Sheet boş veya okunamadı');
      }

      const rows = fullData.values;
      const dataStartIdx = mapping.data_start_row - 1;
      const workouts = [];

      // Column indices
      const dateIdx = getColumnIndex(mapping.date_column);
      const exerciseIdx = getColumnIndex(mapping.exercise_column);
      const notesIdx = getColumnIndex(mapping.notes_column);

      // Parse each row - create SEPARATE record for EACH SET (for progress tracking)
      for (let rowIdx = dataStartIdx; rowIdx < rows.length; rowIdx++) {
        const row = rows[rowIdx];
        if (!row || row.length === 0) continue;

        const rawDate = row[dateIdx];
        const exercise = row[exerciseIdx]?.trim();

        // Skip empty rows
        if (!rawDate || !exercise) continue;

        // Parse date
        const date = parseDate(rawDate);
        if (!date) continue;

        const notes = notesIdx >= 0 ? row[notesIdx]?.trim() || null : null;

        // Create SEPARATE workout for EACH SET
        for (let setNum = 1; setNum <= numSets; setNum++) {
          const repsCol = mapping[`set${setNum}_reps`];
          const weightCol = mapping[`set${setNum}_weight`];

          if (!repsCol && !weightCol) continue;

          const repsIdx = getColumnIndex(repsCol);
          const weightIdx = getColumnIndex(weightCol);

          const repsVal = repsIdx >= 0 ? row[repsIdx] : null;
          const weightVal = weightIdx >= 0 ? row[weightIdx] : null;

          // Skip sets with no data
          if (!repsVal && !weightVal) continue;

          const reps = parseNumber(repsVal);
          const weight = parseNumber(weightVal);

          // Only create record if there's meaningful data
          if (reps || weight) {
            workouts.push({
              date,
              exercise_name: exercise,
              exercise_type: 'fitness',
              sets: 1,
              reps: reps || 0,
              weight: weight || null,
              weight_unit: 'kg',
              notes: notes ? `Set ${setNum} - ${notes}` : `Set ${setNum}`,
            });
          }
        }
      }

      if (workouts.length === 0) {
        toast.error('Hiç workout verisi bulunamadı. Sütun eşleştirmesini kontrol edin.');
        return;
      }

      console.log(`Parsed ${workouts.length} workouts from ${rows.length - dataStartIdx} rows`);

      // Apply date filter if set
      let filteredWorkouts = workouts;
      if (dateFilter) {
        const filterDate = new Date(dateFilter);
        filteredWorkouts = workouts.filter(w => {
          const workoutDate = new Date(w.date);
          return workoutDate >= filterDate;
        });
        console.log(`Date filter applied: ${workouts.length} -> ${filteredWorkouts.length} (after ${dateFilter})`);

        if (filteredWorkouts.length === 0) {
          toast.error(`${dateFilter} tarihinden sonra kayıt bulunamadı.`);
          return;
        }

        toast.success(`${workouts.length - filteredWorkouts.length} kayıt tarih filtresiyle atlandı`, { icon: 'ℹ️' });
      }

      // Exercise Normalization - Local fuzzy matching (fast) or AI (slow)
      let normalizedWorkouts = filteredWorkouts;
      if (normalizeExercises) {
        setIsNormalizing(true);
        toast.loading('Egzersiz isimleri eşleştiriliyor...', { id: 'normalize' });

        try {
          // Get canonical exercise names from static list
          const existingNames = EXERCISES.map(e => e.name);
          console.log(`Using ${existingNames.length} canonical exercises from library`);

          // Get unique exercise names from import
          const uniqueExercises = [...new Set(filteredWorkouts.map(w => w.exercise_name))];
          console.log(`Normalizing ${uniqueExercises.length} unique exercises from Excel`);

          // Create mapping using fuzzy matching
          const mappings = {};       // excelName -> canonicalName
          const muscleMap = {};      // canonicalName -> muscle_group
          let matchedCount = 0;

          // Build muscle map from EXERCISES
          EXERCISES.forEach(e => {
            muscleMap[e.name] = e.muscle;
          });

          // Helper: normalize string for comparison (fix typos only, keep meaningful words)
          const normalize = (str) => str
            .toLowerCase()
            .replace(/İ/gi, 'i')  // Turkish İ
            .replace(/ı/g, 'i')   // Turkish ı
            .replace(/\s*\([^)]*\)\s*/g, ' ') // Remove (22), (4), etc.
            .replace(/\s*\d+x\w+/gi, '') // Remove 3xDrop, 2xmax, etc.
            .replace(/drop\s*set/gi, '')
            .trim()
            .replace(/\s+/g, ' ')  // normalize spaces
            // Fix common typos
            .replace(/dumbell/gi, 'dumbbell')
            .replace(/extansion/gi, 'extension')
            .replace(/riverse/gi, 'reverse')
            .replace(/pushwon/gi, 'pushdown')
            .replace(/hummer/gi, 'hammer')
            .replace(/soulder/gi, 'shoulder')
            .replace(/crossever/gi, 'crossover')
            .replace(/crossver/gi, 'crossover')
            .replace(/macine/gi, 'machine')
            .replace(/straitgh/gi, 'straight')
            .replace(/\bmac\b\.?/gi, 'machine')
            .replace(/\bmach\b\.?/gi, 'machine')
            .replace(/\bpres\b/gi, 'press')
            .replace(/\bext\b\.?/gi, 'extension')
            .replace(/\bches\b/gi, 'chest')
            .replace(/\bd\.\s*/gi, 'dumbbell ')
            .replace(/\bp\.\s*/gi, 'press ')
            .replace(/z\s*bar/gi, 'ez bar')
            .replace(/\s+/g, '')  // remove all spaces for final comparison
            .replace(/[^a-z0-9]/g, ''); // remove non-alphanumeric

          for (const excelName of uniqueExercises) {
            const normalizedExcel = normalize(excelName);

            // Find best match in existing exercises
            let bestMatch = null;
            let bestScore = 0;

            for (const dbName of existingNames) {
              const normalizedDb = normalize(dbName);

              // Exact match after normalization
              if (normalizedExcel === normalizedDb) {
                bestMatch = dbName;
                bestScore = 1;
                break;
              }

              // Contains match
              if (normalizedExcel.includes(normalizedDb) || normalizedDb.includes(normalizedExcel)) {
                const score = Math.min(normalizedExcel.length, normalizedDb.length) /
                              Math.max(normalizedExcel.length, normalizedDb.length);
                if (score > bestScore && score > 0.6) {
                  bestMatch = dbName;
                  bestScore = score;
                }
              }
            }

            if (bestMatch && bestMatch !== excelName) {
              mappings[excelName] = bestMatch;
              matchedCount++;
              console.log(`Mapped: "${excelName}" → "${bestMatch}"`);
            } else {
              mappings[excelName] = excelName; // Keep original
            }
          }

          // Apply mappings to workouts and add muscle_group
          normalizedWorkouts = filteredWorkouts.map(w => {
            const canonicalName = mappings[w.exercise_name] || w.exercise_name;
            return {
              ...w,
              exercise_name: canonicalName,
              muscle_group: muscleMap[canonicalName] || w.muscle_group || null,
            };
          });

          if (matchedCount > 0) {
            toast.success(`${matchedCount} egzersiz ismi düzeltildi`, { id: 'normalize' });
          } else {
            toast.success('Egzersiz isimleri kontrol edildi', { id: 'normalize' });
          }
        } catch (normError) {
          console.error('Normalization error:', normError);
          toast.error(`Normalizasyon atlandı: ${normError.message}`, { id: 'normalize' });
          // Continue with original names
        } finally {
          setIsNormalizing(false);
        }
      }

      // Group workouts by date for scheduled workout creation
      const workoutsByDate = {};
      normalizedWorkouts.forEach(w => {
        if (!workoutsByDate[w.date]) {
          workoutsByDate[w.date] = [];
        }
        workoutsByDate[w.date].push(w);
      });

      const uniqueDates = Object.keys(workoutsByDate).sort();
      console.log(`Found ${uniqueDates.length} unique dates`);

      // Create scheduled workouts for each date
      const scheduledWorkoutMap = {}; // date -> scheduled_workout_id
      const targetClientId = selectedClientId || null;

      toast.loading(`${uniqueDates.length} antrenman günü planlanıyor...`, { id: 'schedule' });

      for (const date of uniqueDates) {
        try {
          // Get dominant muscle group for the day
          const dayWorkouts = workoutsByDate[date];
          const muscleGroups = dayWorkouts
            .map(w => w.muscle_group)
            .filter(Boolean);
          const dominantMuscle = muscleGroups.length > 0
            ? muscleGroups.sort((a,b) =>
                muscleGroups.filter(v => v === b).length - muscleGroups.filter(v => v === a).length
              )[0]
            : null;

          let scheduledWorkout;

          if (targetClientId) {
            // Coach mode - create for client
            scheduledWorkout = await coachRelationships.createClientScheduledWorkout(targetClientId, {
              scheduled_date: date,
              scheduled_time: '09:00:00', // 09:00 - visible in weekly timeline (08:00-22:00)
              duration_minutes: 60,
              exercise_type: 'fitness',
              muscle_group: dominantMuscle,
              notes: `Excel'den import edildi (${dayWorkouts.length} egzersiz)`,
            });

            // Mark as completed since these are past workouts
            if (scheduledWorkout?.id) {
              await coachRelationships.updateClientScheduledWorkout(targetClientId, scheduledWorkout.id, {
                status: 'completed'
              });
            }
          } else {
            // Self mode - create for current user
            scheduledWorkout = await fitness.createScheduledWorkout({
              scheduled_date: date,
              scheduled_time: '09:00:00', // 09:00 - visible in weekly timeline (08:00-22:00)
              duration_minutes: 60,
              exercise_type: 'fitness',
              muscle_group: dominantMuscle,
              notes: `Excel'den import edildi (${dayWorkouts.length} egzersiz)`,
              status: 'completed', // Already completed since past workouts
            });
          }

          if (scheduledWorkout?.id) {
            scheduledWorkoutMap[date] = scheduledWorkout.id;
          }
        } catch (scheduleError) {
          console.error(`Error creating scheduled workout for ${date}:`, scheduleError);
          // Continue with import even if scheduling fails
        }
      }
      toast.dismiss('schedule');
      toast.success(`${Object.keys(scheduledWorkoutMap).length} antrenman günü oluşturuldu`);

      // Add scheduled_workout_id to workouts
      const workoutsWithSchedule = normalizedWorkouts.map(w => ({
        ...w,
        scheduled_workout_id: scheduledWorkoutMap[w.date] || null,
      }));

      // Import with duplicate check
      toast.loading(`${workoutsWithSchedule.length} kayıt içe aktarılıyor...`, { id: 'import2' });
      const result = await fitness.importWorkoutsWithDuplicateCheck(workoutsWithSchedule, targetClientId);
      toast.dismiss('import2');

      if (result.skipped > 0 && result.imported > 0) {
        toast.success(`${result.imported} yeni kayıt eklendi, ${result.skipped} kayıt zaten vardı.`);
      } else if (result.skipped > 0 && result.imported === 0) {
        toast.success(`Tüm ${result.skipped} kayıt zaten mevcut.`, { icon: 'ℹ️' });
      } else {
        toast.success(`${result.imported} workout başarıyla içe aktarıldı!`);
      }

      // Save the mapping for future use
      const mappingToSave = { ...mapping, numSets };
      await fitness.connectSpreadsheet(
        schemaMapping.spreadsheetId,
        schemaMapping.spreadsheetName,
        mappingToSave
      );
      console.log('Mapping saved for future use');

      loadWorkouts();
      loadConnectedSpreadsheets(); // Refresh connected spreadsheets
      closeSchemaMapper();
      onClose();
    } catch (error) {
      toast.dismiss('import');
      toast.dismiss('import2');
      console.error('Import error:', error);
      toast.error('İçe aktarma başarısız: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Parse various date formats
  const parseDate = (rawDate) => {
    if (!rawDate) return null;
    const str = String(rawDate).trim();

    // Try "Wed, Sep 10, 2025" format
    const longMatch = str.match(/\w+,\s+(\w+)\s+(\d+),\s+(\d{4})/);
    if (longMatch) {
      const months = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
                       Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
      const month = months[longMatch[1]];
      const day = longMatch[2].padStart(2, '0');
      const year = longMatch[3];
      if (month) return `${year}-${month}-${day}`;
    }

    // Try ISO format
    const isoMatch = str.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return str.substring(0, 10);

    // Try DD/MM/YYYY or DD.MM.YYYY
    const euMatch = str.match(/(\d{1,2})[\/\.](\d{1,2})[\/\.](\d{4})/);
    if (euMatch) {
      return `${euMatch[3]}-${euMatch[2].padStart(2, '0')}-${euMatch[1].padStart(2, '0')}`;
    }

    return null;
  };

  // Parse number from various formats
  const parseNumber = (val) => {
    if (!val) return null;
    const str = String(val).trim();
    if (!str || str === '-' || str === '?') return null;
    // Remove any non-numeric except . and ,
    const cleaned = str.replace(/[^\d.,]/g, '').replace(',', '.');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  const isValid = mapping.date_column && mapping.exercise_column;

  if (!isOpen || !schemaMapping) return null;

  const previewData = schemaMapping.values || [];

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-6xl max-h-[90vh] bg-gray-900 border border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <button
              onClick={closeSchemaMapper}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Wand2 className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-white">Sütun Eşleştirmesi</h2>
                  {hasSavedMapping && (
                    <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                      Kayıtlı
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400">{schemaMapping.spreadsheetName}</p>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* Preview Panel */}
          <div className="flex-1 border-r border-gray-800 overflow-auto p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Tablo Önizlemesi (İlk 15 satır)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr>
                    <th className="px-2 py-1 text-left text-gray-500 border-b border-gray-800 sticky left-0 bg-gray-900">#</th>
                    {columns.map(col => {
                      // Check if this column is selected for anything
                      const isSelected = Object.values(mapping).includes(col);
                      return (
                        <th
                          key={col}
                          className={`
                            px-2 py-1 text-left border-b border-gray-800 whitespace-nowrap
                            ${isSelected ? 'bg-orange-500/20 text-orange-400' : 'text-gray-400'}
                          `}
                        >
                          <div className="font-bold">{col}</div>
                          <div className="font-normal text-2xs truncate max-w-[80px]">
                            {headerValues[col] || ''}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 15).map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className={rowIdx === mapping.header_row - 1 ? 'bg-blue-500/10' : ''}
                    >
                      <td className="px-2 py-1 text-gray-500 border-b border-gray-800/50 sticky left-0 bg-gray-900">
                        {rowIdx + 1}
                      </td>
                      {columns.map((col, colIdx) => {
                        const isSelected = Object.values(mapping).includes(col);
                        return (
                          <td
                            key={col}
                            className={`
                              px-2 py-1 border-b border-gray-800/50 whitespace-nowrap
                              ${isSelected ? 'bg-orange-500/10 text-white' : 'text-gray-300'}
                            `}
                          >
                            {row[colIdx] || ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {previewData.length > 15 && (
              <p className="text-xs text-gray-500 mt-2">+ {previewData.length - 15} satır daha...</p>
            )}
          </div>

          {/* Mapping Panel */}
          <div className="w-96 p-4 overflow-y-auto">
            <h3 className="text-sm font-medium text-gray-400 mb-4">Sütun Eşleştirmesi</h3>

            {/* Row settings */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Başlık Satırı</label>
                <select
                  value={mapping.header_row}
                  onChange={(e) => setMapping(prev => ({ ...prev, header_row: parseInt(e.target.value) }))}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n}. satır</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Veri Başlangıcı</label>
                <select
                  value={mapping.data_start_row}
                  onChange={(e) => setMapping(prev => ({ ...prev, data_start_row: parseInt(e.target.value) }))}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                >
                  {[2, 3, 4, 5, 6, 7, 8].map(n => (
                    <option key={n} value={n}>{n}. satır</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Required Fields */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Tarih <span className="text-red-400">*</span>
                </label>
                <select
                  value={mapping.date_column}
                  onChange={(e) => handleColumnSelect('date_column', e.target.value)}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-sm ${
                    mapping.date_column ? 'border-orange-500/50 text-white' : 'border-gray-700 text-gray-400'
                  }`}
                >
                  <option value="">Seçiniz...</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col} - {headerValues[col] || `Sütun ${col}`}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  Egzersiz Adı <span className="text-red-400">*</span>
                </label>
                <select
                  value={mapping.exercise_column}
                  onChange={(e) => handleColumnSelect('exercise_column', e.target.value)}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-sm ${
                    mapping.exercise_column ? 'border-orange-500/50 text-white' : 'border-gray-700 text-gray-400'
                  }`}
                >
                  <option value="">Seçiniz...</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col} - {headerValues[col] || `Sütun ${col}`}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Notlar (Opsiyonel)</label>
                <select
                  value={mapping.notes_column}
                  onChange={(e) => handleColumnSelect('notes_column', e.target.value)}
                  className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-sm ${
                    mapping.notes_column ? 'border-orange-500/50 text-white' : 'border-gray-700 text-gray-400'
                  }`}
                >
                  <option value="">Seçiniz...</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col} - {headerValues[col] || `Sütun ${col}`}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Coach Selection - for clients */}
            {isLoadingRelationships ? (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-center gap-2 text-blue-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>İlişkiler yükleniyor...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Show coach selector for clients */}
                {coaches.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <label className="block text-sm text-blue-300 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-blue-400" />
                        <span>Koç Seçimi</span>
                        {selectedCoachId && <span className="text-blue-400">✓</span>}
                      </div>
                    </label>
                    <select
                      value={selectedCoachId}
                      onChange={(e) => setSelectedCoachId(e.target.value)}
                      className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-sm focus:outline-none ${
                        selectedCoachId
                          ? 'border-blue-500/50 text-white'
                          : 'border-gray-700 text-gray-400'
                      }`}
                    >
                      <option value="">Koç seçiniz...</option>
                      {coaches.map((rel) => (
                        <option key={rel.coach_id} value={rel.coach_id}>
                          {rel.coach?.display_name || rel.coach?.full_name || rel.coach?.email || rel.coach_id}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-blue-400/70 mt-1">
                      {selectedCoachId
                        ? 'Verileriniz seçili koç tarafından görülebilecek'
                        : 'Hangi koçunuzun bu verileri göreceğini seçin'}
                    </p>
                  </div>
                )}

                {/* Show client selector for coaches */}
                {clients.length > 0 && (
                  <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <label className="block text-sm text-green-300 mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-green-400" />
                        <span>Danışan Seçimi</span>
                        {selectedClientId && <span className="text-green-400">✓</span>}
                      </div>
                    </label>
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className={`w-full px-3 py-2 bg-gray-800 border rounded-lg text-sm focus:outline-none ${
                        selectedClientId
                          ? 'border-green-500/50 text-white'
                          : 'border-gray-700 text-gray-400'
                      }`}
                    >
                      <option value="">Kendin için içe aktar</option>
                      {clients.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.client?.full_name || client.client?.email || client.client_id}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-green-400/70 mt-1">
                      {selectedClientId
                        ? 'Veriler seçili danışanın hesabına kaydedilecek'
                        : 'Veriler kendi hesabınıza kaydedilecek'}
                    </p>
                  </div>
                )}

                {/* No relationships message */}
                {coaches.length === 0 && clients.length === 0 && (
                  <div className="mb-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
                    <p className="text-sm text-gray-400">
                      Koç veya danışan ilişkiniz bulunmuyor. Veriler kendi hesabınıza kaydedilecek.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Set Count Selector */}
            <div className="flex items-center justify-between mb-3 p-3 bg-gray-800/50 rounded-lg">
              <span className="text-sm text-gray-300">Set Sayısı</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setNumSets(prev => Math.max(1, prev - 1))}
                  disabled={numSets <= 1}
                  className="p-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center text-white font-medium">{numSets}</span>
                <button
                  onClick={() => setNumSets(prev => Math.min(5, prev + 1))}
                  disabled={numSets >= 5}
                  className="p-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Set Mappings */}
            <div className="space-y-3">
              {Array.from({ length: numSets }, (_, i) => i + 1).map(setNum => (
                <div key={setNum} className="p-3 bg-gray-800/30 rounded-lg border border-gray-700/50">
                  <p className="text-xs font-medium text-orange-400 mb-2">Set {setNum}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-2xs text-gray-500 mb-1">Tekrar</label>
                      <select
                        value={mapping[`set${setNum}_reps`]}
                        onChange={(e) => handleColumnSelect(`set${setNum}_reps`, e.target.value)}
                        className={`w-full px-2 py-1.5 bg-gray-800 border rounded text-xs ${
                          mapping[`set${setNum}_reps`] ? 'border-orange-500/50 text-white' : 'border-gray-700 text-gray-400'
                        }`}
                      >
                        <option value="">-</option>
                        {columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-2xs text-gray-500 mb-1">Ağırlık</label>
                      <select
                        value={mapping[`set${setNum}_weight`]}
                        onChange={(e) => handleColumnSelect(`set${setNum}_weight`, e.target.value)}
                        className={`w-full px-2 py-1.5 bg-gray-800 border rounded text-xs ${
                          mapping[`set${setNum}_weight`] ? 'border-orange-500/50 text-white' : 'border-gray-700 text-gray-400'
                        }`}
                      >
                        <option value="">-</option>
                        {columns.map(col => (
                          <option key={col} value={col}>{col}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Date Filter */}
            <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
              <label className="block text-sm text-orange-300 mb-2">
                📅 Tarih Filtresi (Opsiyonel)
              </label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
              />
              <p className="text-xs text-orange-400/70 mt-1">
                Sadece bu tarihten sonraki kayıtları içe aktar
              </p>
            </div>

            {/* AI Normalization Toggle */}
            <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
              <label className="flex items-center justify-between cursor-pointer">
                <div className="flex items-center gap-2">
                  <Wand2 className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-300">AI ile Egzersiz Normalizasyonu</span>
                </div>
                <input
                  type="checkbox"
                  checked={normalizeExercises}
                  onChange={(e) => setNormalizeExercises(e.target.checked)}
                  className="w-4 h-4 accent-purple-500"
                />
              </label>
              <p className="text-xs text-purple-400/70 mt-1">
                Yazım hatalarını düzeltir ve egzersiz isimlerini standartlaştırır
              </p>
            </div>

            {/* Help Text */}
            <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-xs text-blue-300">
                Her set için Tekrar ve Ağırlık sütunlarını ayrı ayrı seçin.
                Örnek: E sütunu Set1 Tekrar, F sütunu Set1 Ağırlık...
              </p>
            </div>

            {/* Validation message */}
            {!isValid && (
              <p className="text-xs text-amber-400 mt-3">
                Tarih ve Egzersiz sütunlarını seçmeniz gerekiyor.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-800">
          <p className="text-sm text-gray-500">
            Seçili sütunlar turuncu ile işaretlenir
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={closeSchemaMapper}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              İptal
            </button>
            <button
              onClick={handleImport}
              disabled={!isValid || isImporting || isNormalizing}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {isImporting || isNormalizing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Import className="w-4 h-4" />
              )}
              <span>{isNormalizing ? 'Normalize Ediliyor...' : 'İçe Aktar'}</span>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default SchemaMapperModal;
